import { Stack, tokens, Typography } from '@cast/design-system';
import {
  KentKentrollerConditionStatus,
  KentKentrollerStatusDetails,
} from '@cast/types-kent';

import { CopyToClipboard } from 'components/clickable-actions/CopyToClipboard';
import { DerivedKentrollerStatusChip } from 'features/kent/components/DerivedKentrollerStatusChip';
import { getKentrollerConditionStatus } from 'features/kent/utils';

import { METADATA_GRID_TEMPLATE_CSS_VAR } from './_constants';
import { MetadataField } from './MetadataField';

const FAILED_CRITICAL_CONDITION_COLOR = tokens.theme.fg.status.error;
const FAILED_NON_CRITICAL_CONDITION_COLOR = tokens.theme.fg.status.warning;
const UNKNOWN_LABEL = 'Unknown';

const getMothershipStreamConditionStatusText = (
  status: KentKentrollerConditionStatus
) => {
  switch (status) {
    case 'KENT_CONDITION_STATUS_TRUE':
      return 'Connected';
    case 'KENT_CONDITION_STATUS_FALSE':
      return 'Lost';
    default:
      return UNKNOWN_LABEL;
  }
};

const getConfigSyncConditionStatusText = (
  status: KentKentrollerConditionStatus
) => {
  switch (status) {
    case 'KENT_CONDITION_STATUS_TRUE':
      return 'Synced';
    case 'KENT_CONDITION_STATUS_FALSE':
      return 'Not synced';
    default:
      return UNKNOWN_LABEL;
  }
};

const getKentrollerVersionDisplayValue = (
  statusDetails: KentKentrollerStatusDetails
) => {
  const status = getKentrollerConditionStatus({
    kentrollerStatusDetails: statusDetails,
    conditionType: 'KENT_CONDITION_TYPE_KENTROLLER_VERSION_VALID',
  });
  const { kentrollerVersion } = statusDetails;

  switch (status) {
    case 'KENT_CONDITION_STATUS_TRUE':
    case 'KENT_CONDITION_STATUS_UNKNOWN':
      return kentrollerVersion ?? UNKNOWN_LABEL;
    case 'KENT_CONDITION_STATUS_FALSE':
      return kentrollerVersion
        ? `${kentrollerVersion} (outdated)`
        : UNKNOWN_LABEL;
    default:
      return UNKNOWN_LABEL;
  }
};

type Props = {
  statusDetails: KentKentrollerStatusDetails;
};

export const KentrollerStatusRow = ({ statusDetails }: Props) => {
  const mothershipStreamConditionStatus = getKentrollerConditionStatus({
    kentrollerStatusDetails: statusDetails,
    conditionType: 'KENT_CONDITION_TYPE_KENT_STREAM_CONNECTED',
  });

  const kentrollerVersionConditionStatus = getKentrollerConditionStatus({
    kentrollerStatusDetails: statusDetails,
    conditionType: 'KENT_CONDITION_TYPE_KENTROLLER_VERSION_VALID',
  });

  const configSyncConditionStatus = getKentrollerConditionStatus({
    kentrollerStatusDetails: statusDetails,
    conditionType: 'KENT_CONDITION_TYPE_KENTROLLER_CONFIG_SYNCED',
  });

  const { kentrollerVersion } = statusDetails;

  return (
    <Stack
      width="100%"
      data-testid="kent-kentroller-status-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: `var(${METADATA_GRID_TEMPLATE_CSS_VAR})`,
      }}
    >
      <MetadataField label="Kentroller status" testId="kent-kentroller-status">
        <DerivedKentrollerStatusChip
          statusDetails={statusDetails}
          size="small"
          variant="strong"
          showLastFailedConditionDetailsTooltip={true}
        />
      </MetadataField>

      <MetadataField
        label="Mothership stream"
        withDivider
        testId="kent-kentroller-status-mothership-stream"
      >
        <Typography
          variant="P14M"
          sx={{
            color:
              mothershipStreamConditionStatus === 'KENT_CONDITION_STATUS_FALSE'
                ? FAILED_CRITICAL_CONDITION_COLOR
                : undefined,
          }}
        >
          {getMothershipStreamConditionStatusText(
            mothershipStreamConditionStatus
          )}
        </Typography>
      </MetadataField>

      <MetadataField
        label="Kentroller version"
        withDivider
        testId="kent-kentroller-status-kentroller-version"
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography
            variant="P14M"
            noWrap
            sx={{
              color:
                kentrollerVersionConditionStatus ===
                'KENT_CONDITION_STATUS_FALSE'
                  ? FAILED_NON_CRITICAL_CONDITION_COLOR
                  : undefined,
            }}
          >
            {getKentrollerVersionDisplayValue(statusDetails)}
          </Typography>
          {kentrollerVersion ? (
            <CopyToClipboard
              text={kentrollerVersion}
              iconSize={16}
              testId="copy-kentroller-version"
            />
          ) : null}
        </Stack>
      </MetadataField>

      <MetadataField
        label="Config sync"
        withDivider
        testId="kent-kentroller-status-config-sync"
      >
        <Typography
          variant="P14M"
          sx={{
            color:
              configSyncConditionStatus === 'KENT_CONDITION_STATUS_FALSE'
                ? FAILED_CRITICAL_CONDITION_COLOR
                : undefined,
          }}
        >
          {getConfigSyncConditionStatusText(configSyncConditionStatus)}
        </Typography>
      </MetadataField>
    </Stack>
  );
};
